import type { SiteId } from '@hotjar/site';

import * as SlackApi from './api';
import type { GenericSuccessData } from './api/common';

export const ACTION_NAME = 'integrationOAuthCallback';

export async function connect(siteId: SiteId): Promise<GenericSuccessData> {
  const redirectUri = `${window.location.origin}/oauth?integration=slack`;
  const {
    data: { success, slack_enrollment: slackEnrollment },
  } = await SlackApi.getEnrollment(siteId);

  if (!success) {
    throw new Error('Unable to get slack enrollment data.');
  }

  const { client_id: clientId, scope } = slackEnrollment;

  const openedWindow = window.open(
    `https://slack.com/oauth/v2/authorize?client_id=${clientId}&scope=${scope}&redirect_uri=${redirectUri}`,
    'oauthHandler',
    'height=780,width=620',
  );

  if (openedWindow === null) {
    return Promise.reject(new Error('No authentication window opened.'));
  }

  return new Promise((resolve, reject) => {
    // We define timer here do that we have a reference to timer in the `onMessage` handler.
    let timer: ReturnType<typeof setTimeout>;

    const onMessage = async (event: MessageEvent) => {
      if (event.data.action !== ACTION_NAME) {
        return;
      }

      // This message listener should only ever fire once at most.
      window.removeEventListener('message', onMessage);

      const { code, performanceTiming } = event.data.data;

      if (!code) {
        const error = new Error('Missing or empty code value.');
        reject(error);

        return;
      }

      clearInterval(timer);
      openedWindow.close();

      const { data } = await SlackApi.setEnrollment(siteId, {
        code,
        redirect_uri: redirectUri,
      });

      if (data.success) {
        resolve(data);
      } else {
        reject(new Error('Unknown error occurred.'));
      }

      if (
        performanceTiming &&
        performanceTiming.loadEventEnd &&
        performanceTiming.navigationStart &&
        window.DDLogs &&
        window.DDLogs.logger &&
        typeof window.DDLogs.logger.log === 'function'
      ) {
        window.DDLogs.logger.log('slack_oauth_load', {
          time: performanceTiming.loadEventEnd - performanceTiming.navigationStart,
        });
      }
    };

    /* We check for the window being closed and reject, in case users abandon it at the slack authentication step.
     * We need to define the interval after the `onMessage` function since we need a reference to `onMessage`.
     * This is so that we can cancel the message listener if the user closes the popup window manually.
     */
    timer = setInterval(() => {
      if (openedWindow.closed) {
        window.removeEventListener('message', onMessage);
        clearInterval(timer);
        reject(new Error('Slack authentication cancelled.'));
      }
    }, 1000);

    window.addEventListener('message', onMessage);
  });
}
